/**
 * Centralized event location display formatter.
 * Uses normalized fields (location_city, location_state, location_zip)
 * with graceful fallback for legacy combined formats.
 *
 * Preferred display: "City, ST" | "City" | "ZIP" | ""
 */

export function formatEventLocation(event) {
  if (!event) return '';
  const city = event.location_city || '';
  const state = event.location_state || '';
  const zip = event.location_zip || '';

  // Normalized path: city + state
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (zip) return zip;
  return '';
}

/**
 * Customer-facing browse/header location formatter.
 *
 * Render-time safety net: even if a stale admin-area label (e.g. "District 4")
 * somehow slipped past geocode normalization and is sitting in localStorage,
 * the UI will degrade gracefully through the cascade below instead of leaking
 * raw admin labels into "Providers near …" copy.
 *
 * Rules mirror `isAcceptableCityName` from LocationContext — keep in sync.
 *
 * Returns one of:
 *   "Near Gulfport, MS"
 *   "Near Gulfport"
 *   "In Mississippi"
 *   "In your area"
 */
const HEADER_REJECT_PATTERNS = [
  /^(District|Ward|Precinct|Subdistrict|Zone|Sector)\s+\d+\b/i,
  /^Council\s+District\b/i,
  /^Census\s+(Tract|Block(\s+Group)?|Designated\s+Place)\b/i,
  /^Unincorporated\b/i,
  /\s+(Parish|County|Borough|Township)$/i,
  /^(Unknown|N\/A|Unspecified|None)$/i,
  /^\d+$/,
];

const _isSafeCity = (name) => {
  if (!name || typeof name !== 'string') return false;
  const t = name.trim();
  if (!t) return false;
  return !HEADER_REJECT_PATTERNS.some((p) => p.test(t));
};

export function formatLocationHeader({ city, state } = {}) {
  const safeCity = _isSafeCity(city) ? city.trim() : '';
  const safeState = state && typeof state === 'string' ? state.trim() : '';
  if (safeCity && safeState) return `Near ${safeCity}, ${safeState}`;
  if (safeCity) return `Near ${safeCity}`;
  if (safeState) return `In ${safeState}`;
  return 'In your area';
}

/**
 * Returns a longer display: "City, ST 12345" or best available.
 */
export function formatEventLocationFull(event) {
  if (!event) return '';
  const short = formatEventLocation(event);
  const zip = event.location_zip || '';
  if (short && zip && !short.includes(zip)) return `${short} ${zip}`;
  return short || zip || '';
}
